import { FC } from 'react';
import { List, Box, Typography } from '@mui/material';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import ScheduleItemRow from '@/components/modals/schedule/ScheduleItemRow';
import type { ScheduleItem, Category, EventData } from '@/types';
import { useI18nContext } from '@/i18n/i18n-react';

interface ScheduleItemListProps {
    items: (ScheduleItem & { displayStartTime: string; displayEndTime: string })[];
    canEdit: boolean;
    reorderEnabled: boolean;
    editItemId: number | null;
    isDeleting: boolean;
    categories: Category[];
    eventData?: EventData;
    onEdit: (item: ScheduleItem) => void;
    onDelete: (id: number) => void;
    onReorder: (activeId: number, overId: number) => void;
    onToggleHighlight: (itemId: number, field: 'title' | 'responsible' | 'note') => void;
    formatTime: (time?: string) => string;
}

const ScheduleItemList: FC<ScheduleItemListProps> = ({
    items,
    reorderEnabled,
    canEdit,
    editItemId,
    isDeleting,
    categories,
    eventData,
    onEdit,
    onDelete,
    onReorder,
    onToggleHighlight,
    formatTime,
}) => {
    const { LL } = useI18nContext();
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // 8px movement required before drag starts
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            onReorder(active.id as number, over.id as number);
        }
    };

    if (items.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: { xs: 2, sm: 4 } }}>
                <Typography
                    sx={{
                        color: 'text.secondary',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                    }}
                >
                    {LL.scheduleModal.noScheduleItems()}
                </Typography>
                {canEdit && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            mt: 1,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                    >
                        {LL.scheduleModal.addFirstItem()}
                    </Typography>
                )}
            </Box>
        );
    }

    const listContent = (
        <List sx={{ p: 0 }}>
            {items.map((item) => (
                <ScheduleItemRow
                    key={item.id}
                    item={item}
                    canEdit={canEdit}
                    reorderEnabled={reorderEnabled}
                    editItemId={editItemId}
                    isDeleting={isDeleting}
                    categories={categories}
                    eventData={eventData}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onToggleHighlight={onToggleHighlight}
                    formatTime={formatTime}
                />
            ))}
        </List>
    );

    if (!reorderEnabled) {
        return listContent;
    }

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
                items={items.map((item) => item.id!)}
                strategy={verticalListSortingStrategy}
            >
                {listContent}
            </SortableContext>
        </DndContext>
    );
};

export default ScheduleItemList;
